import React, { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Search } from 'lucide-react';
import { loadGoogleMaps } from '../utils/googleMaps';

// Re-export for backward compatibility (callers that import from here)
export const loadGoogleMapsScript = (storeApiKey?: string): Promise<void> => loadGoogleMaps(storeApiKey);

export interface AddressFields {
  address?: string;
  street_address?: string;
  street_number?: string;
  city?: string;
  postal_code?: string;
  area?: string;
  country?: string;
  formatted_address?: string;
  latitude?: number | null;
  longitude?: number | null;
}

interface AddressAutocompleteProps {
  /** Current address field values */
  value: AddressFields;
  /** Called when any address field changes */
  onChange: (fields: AddressFields) => void;
  /** Label for the search input */
  label?: string;
  /** Placeholder for the search input */
  placeholder?: string;
  /** Whether the fields are read-only */
  disabled?: boolean;
  /** Country restriction for autocomplete (default: 'gr') */
  countryRestriction?: string;
  /** Whether to show the map preview */
  showMap?: boolean;
  /** CSS class for the container */
  className?: string;
  /** Prefix for field labels (e.g. "Κεντρικά - ") */
  labelPrefix?: string;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  label: labelProp,
  placeholder: placeholderProp,
  disabled = false,
  countryRestriction = 'gr',
  showMap = true,
  className = '',
  labelPrefix = ''
}) => {
  const { t } = useTranslation('common');
  const label = labelProp ?? t('address');
  const placeholder = placeholderProp ?? t('searchAddress');
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const handlePlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry?.location) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    // Parse address components
    let streetNumber = '';
    let streetName = '';
    let areaName = '';
    let cityName = '';
    let postalCode = '';
    let countryCode = countryRestriction.toUpperCase();

    place.address_components?.forEach(component => {
      const types = component.types;
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      } else if (types.includes('route')) {
        streetName = component.long_name;
      } else if (types.includes('neighborhood') || types.includes('sublocality') || types.includes('sublocality_level_1')) {
        areaName = component.long_name;
      } else if (types.includes('locality')) {
        cityName = component.long_name;
      } else if (types.includes('postal_code')) {
        postalCode = component.long_name.replace(/\s/g, '');
      } else if (types.includes('country')) {
        countryCode = component.short_name;
      }
    });

    // Build display address: "Οδός Αριθμός" format
    const displayAddress = streetName + (streetNumber ? ' ' + streetNumber : '');

    onChange({
      address: displayAddress || value.address,
      street_address: streetName,
      street_number: streetNumber,
      city: cityName || value.city,
      postal_code: postalCode || value.postal_code,
      area: areaName || value.area,
      country: countryCode,
      formatted_address: place.formatted_address || '',
      latitude: lat,
      longitude: lng
    });
  }, [onChange, value, countryRestriction]);

  // Initialize Places Autocomplete
  useEffect(() => {
    if (!inputRef.current || !window.google?.maps?.places || autocompleteRef.current) return;

    autocompleteRef.current = new google.maps.places.Autocomplete(
      inputRef.current,
      {
        types: ['address'],
        componentRestrictions: { country: countryRestriction },
        fields: ['address_components', 'geometry', 'formatted_address']
      }
    );

    autocompleteRef.current.addListener('place_changed', handlePlaceChanged);

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [countryRestriction, handlePlaceChanged]);

  // Re-init autocomplete if Google Maps loads after mount
  useEffect(() => {
    if (autocompleteRef.current) return;

    const checkGoogleMaps = setInterval(() => {
      if (window.google?.maps?.places && inputRef.current) {
        clearInterval(checkGoogleMaps);
        autocompleteRef.current = new google.maps.places.Autocomplete(
          inputRef.current,
          {
            types: ['address'],
            componentRestrictions: { country: countryRestriction },
            fields: ['address_components', 'geometry', 'formatted_address']
          }
        );
        autocompleteRef.current.addListener('place_changed', handlePlaceChanged);
      }
    }, 500);

    return () => clearInterval(checkGoogleMaps);
  }, [countryRestriction, handlePlaceChanged]);

  const inputClass = "w-full px-3.5 py-2.5 text-sm border border-line-strong rounded-xl bg-surface-2 text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500";

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Search Input */}
      <div>
        <label className="block text-sm font-medium text-t2 mb-1.5">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            {label}
          </div>
        </label>
        <input
          ref={inputRef}
          type="text"
          className={`${inputClass} ${disabled ? 'bg-surface-2 dark:bg-surface-2/50 text-t4 cursor-not-allowed' : ''}`}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="one-time-code"
          defaultValue={value.formatted_address || value.address || ''}
        />
      </div>

      {/* Structured fields (2-column grid) */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-t3 mb-1">{labelPrefix}{t('street')}</label>
          <input
            type="text"
            className={inputClass}
            value={value.street_address || ''}
            onChange={(e) => onChange({ ...value, street_address: e.target.value })}
            placeholder={t('streetPlaceholder')}
            disabled={disabled}
            autoComplete="one-time-code"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-t3 mb-1">{labelPrefix}{t('streetNumber')}</label>
          <input
            type="text"
            className={inputClass}
            value={value.street_number || ''}
            onChange={(e) => onChange({ ...value, street_number: e.target.value })}
            placeholder={t('streetNumberPlaceholder')}
            disabled={disabled}
            autoComplete="one-time-code"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-t3 mb-1">{labelPrefix}{t('city')}</label>
          <input
            type="text"
            className={inputClass}
            value={value.city || ''}
            onChange={(e) => onChange({ ...value, city: e.target.value })}
            placeholder={t('cityPlaceholder')}
            disabled={disabled}
            autoComplete="one-time-code"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-t3 mb-1">{labelPrefix}{t('postalCode')}</label>
          <input
            type="text"
            className={inputClass}
            value={value.postal_code || ''}
            onChange={(e) => onChange({ ...value, postal_code: e.target.value })}
            placeholder={t('postalCodePlaceholder')}
            disabled={disabled}
            autoComplete="one-time-code"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-t3 mb-1">{labelPrefix}{t('area')}</label>
          <input
            type="text"
            className={inputClass}
            value={value.area || ''}
            onChange={(e) => onChange({ ...value, area: e.target.value })}
            placeholder={t('areaPlaceholder')}
            disabled={disabled}
            autoComplete="one-time-code"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-t3 mb-1">{labelPrefix}{t('country')}</label>
          <input
            type="text"
            className={inputClass}
            value={value.country || 'GR'}
            onChange={(e) => onChange({ ...value, country: e.target.value })}
            placeholder="GR"
            disabled={disabled}
            autoComplete="one-time-code"
          />
        </div>
      </div>

      {/* Map preview */}
      {showMap && value.latitude && value.longitude && (
        <div className="rounded-xl overflow-hidden border border-line-strong">
          <div className="h-36 bg-surface-2 flex items-center justify-center">
            <iframe
              title="map-preview"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${value.latitude},${value.longitude}&zoom=16`}
            />
          </div>
          <div className="px-2.5 py-1.5 bg-surface-2 text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {Number(value.latitude)?.toFixed(6)}, {Number(value.longitude)?.toFixed(6)}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
